import {
  Typography as MuiTypography,
  IconButton as MuiIconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  Autorenew as MuiAutorenewIcon,
  Cancel as MuiCancelIcon,
  Info as MuiInfoIcon,
  Warning as MuiWarningIcon,
} from "@mui/icons-material";
import { Trans } from "react-i18next";
import {
  OutreachInitiationModes,
  OutreachStatuses,
  URLs,
} from "app/shared/constants";
import { conductOutreachPageContentStyles } from "./ConductOutreachPageStyles";
import Export from "app/shared/UI/Export";
import ErrorDialog from "app/shared/UI/Dialog/ErrorDialog";
import OutreachDetails from "./OutreachDetails";
import { headerStyles, multiLineCell } from "app/shared/UI/sharedStyles";

export const defaultTableColumnHeadersHistory = (
  t,
  displayFailureSummaryDialog,
  handleOutreachErrorContent,
  failureSummaryDialog,
  failedOutreach,
  closeFailureSummaryDialog,
  errorsList,
  errorSummaryContent,
  isLoading,
  displayOutreachQuantityDialog,
  openDialog,
  selectedOutreach,
  closeOutreachQuantityDialog,
  study,
  refresh,
  setRefresh,
  handleClickCancel,
  setCancelOutreachRow,
) => [
  {
    field: "index",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnWaveNumber" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return <MuiTypography sx={multiLineCell}>{params.value}</MuiTypography>;
      }
      return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
    },
    minWidth: 10,
    sortable: false,
    flex: 1,
  },
  {
    field: "initiationMode",
    renderHeader: () => {
      return (
        <MuiTooltip
          title={t("ConductOutreachPage.tooltipRecurringSchedule")}
          arrow
        >
          <MuiAutorenewIcon
            sx={{
              ...headerStyles,
              ...conductOutreachPageContentStyles.recurringIcon,
            }}
            color="primary"
            fontSize="small"
          />
        </MuiTooltip>
      );
    },
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return (
          <MuiTypography sx={multiLineCell}>
            {params.row.initiationMode === OutreachInitiationModes.SYSTEM ? (
              <MuiTooltip
                title={t("ConductOutreachPage.tooltipRecurringSchedule")}
                arrow
              >
                <MuiAutorenewIcon
                  sx={conductOutreachPageContentStyles.recurringIcon}
                  color="primary"
                  fontSize="small"
                />
              </MuiTooltip>
            ) : null}
          </MuiTypography>
        );
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 60,
    flex: 1,
  },
  {
    field: "generationType",
    renderHeader: () => {
      return (
        <MuiTypography style={headerStyles}>
          <Trans i18nKey="ConductOutreachPage.tableColumnMethod" />
        </MuiTypography>
      );
    },
    renderCell: (params) => {
      if (params.value === "HONEST_BROKER_GENERATED") {
        return (
          <MuiTooltip
            title={t("ConductOutreachPage.tooltipHonestBroker")}
            arrow
          >
            <MuiTypography sx={multiLineCell}>
              {t("ConductOutreachPage.rowGenerationTypeHonestBroker")}
            </MuiTypography>
          </MuiTooltip>
        );
      }
      if (params.value === "STUDY_TEAM_GENERATED") {
        return (
          <MuiTooltip
            title={t("ConductOutreachPage.tooltipSelfService")}
            arrow
          >
            <MuiTypography sx={multiLineCell}>
              {t("ConductOutreachPage.rowGenerationTypeSelfService")}
            </MuiTypography>
          </MuiTooltip>
        );
      }
    },
    minWidth: 80,
    flex: 1,
    sortable: false,
  },
  {
    field: "conductedBy",
    renderHeader: () => {
      return (
        <MuiTypography style={headerStyles}>
          <Trans i18nKey="ConductOutreachPage.tableColumnInitiator" />
        </MuiTypography>
      );
    },
    renderCell: (row) => {
      return (
        <MuiTypography sx={multiLineCell}>
          {row.value?.displayName}
        </MuiTypography>
      );
    },
    minWidth: 160,
    flex: 1,
  },
  {
    field: "waveName",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnWaveName" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.status &&
            row.status !== "FAILED" &&
            row.status !== "SCHEDULED" &&
            row.status !== "CANCELED" ? (
              <Export
                displayName={row.waveName}
                // TODO: use wavename_<filename>
                downloadName={row.waveName + ".csv"}
                channel={row.channel}
                exportURL={`${URLs.GET_OUTREACH_URL_BASE}/${row.id}/file`}
              />
            ) : (
              row.waveName
            )}
          </MuiTypography>
        );
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 150,
    flex: 1,
  },
  {
    field: "status",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnWaveStatus" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.status &&
            (row.status === OutreachStatuses.FAILED ||
              row.status === OutreachStatuses.FINISHED_WITH_ERROR ||
              row.status === OutreachStatuses.GENERATED_WITH_ERROR) ? (
              <div
                style={conductOutreachPageContentStyles.cellWithInfoContainer}
              >
                {row.status}
                <MuiWarningIcon
                  sx={conductOutreachPageContentStyles.warning}
                  onClick={() => {
                    displayFailureSummaryDialog(row.index);
                    handleOutreachErrorContent(row);
                  }}
                  fontSize="small"
                />
                {failureSummaryDialog && row.index === failedOutreach && (
                  <ErrorDialog
                    onClose={closeFailureSummaryDialog}
                    open={failureSummaryDialog}
                    errorsList={errorsList}
                    title={t(`OutreachFailureSummary.title`)}
                    primaryContent={errorSummaryContent.primaryContent}
                    secondaryContent={errorSummaryContent.secondaryContent}
                    displayDynamicPrimarySubHeading={true}
                    isLoading={isLoading}
                  />
                )}
              </div>
            ) : (
              <div
                style={conductOutreachPageContentStyles.cellWithInfoContainer}
              >
                {row.status || t(`ConductOutreachPage.tableColumnNA`)}
              </div>
            )}
          </MuiTypography>
        );
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 200,
    flex: 1,
  },
  {
    field: "channel",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnChannel" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return <MuiTypography sx={multiLineCell}>{params.value}</MuiTypography>;
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 80,
    flex: 1,
  },
  {
    field: "cohortSource",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnCohortSource" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.importRequest?.filename}
          </MuiTypography>
        );
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 150,
    flex: 1,
  },
  {
    field: "conductedDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnTime" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return (
          <MuiTypography sx={multiLineCell}>
            {params.value ? new Date(params.value).toLocaleString() : null}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {t(`ConductOutreachPage.tableTotalRowTotals`)}
          </MuiTypography>
        );
      }
    },
    minWidth: 110,
    flex: 1,
  },
  {
    field: "conductedDay",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnDay" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return <MuiTypography sx={multiLineCell}>{params.value}</MuiTypography>;
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 110,
    flex: 1,
  },
  {
    field: "adjustedOutreachQuantity",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnCount" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            <div style={conductOutreachPageContentStyles.cellWithInfoContainer}>
              {row.quantity}

              <MuiInfoIcon
                color="primary"
                onClick={() => displayOutreachQuantityDialog(row.index)}
                fontSize="small"
              />
              {openDialog && row.index === selectedOutreach && (
                <OutreachDetails
                  onClose={closeOutreachQuantityDialog}
                  open={openDialog}
                  outreach={{
                    id: row.id,
                    status: row.status,
                    quantity: row.quantity,
                    requestedQuantity: row.requestedQuantity,
                    outreachSummary: row.outreachSummary,
                    responseSummary: row.responseSummary,
                    subjectSummary: row.subjectSummary,
                    channel: row.channel,
                    subchannel: row.subchannel,
                    waveName: row.waveName,
                    studyId: study.id,
                    programURL: row.emailProgramUrl,
                    templateName: row.parentEmailTemplateName,
                  }}
                  refresh={refresh}
                  setRefresh={setRefresh}
                  summaryAccrual={study.protocolSummaryAccrual}
                />
              )}
            </div>
          </MuiTypography>
        );
      } else {
        return (
          <div style={conductOutreachPageContentStyles.cellWithInfoContainer}>
            {study && study.outreachSummary.adjustedOutreachQuantity}
            <MuiInfoIcon
              color="primary"
              onClick={() => displayOutreachQuantityDialog()}
              fontSize="small"
            />
            {openDialog && (
              <OutreachDetails
                onClose={closeOutreachQuantityDialog}
                open={openDialog}
                outreach={{
                  id: study.id,
                  quantity: study.outreachSummary.adjustedOutreachQuantity,
                  requestedQuantity: study.outreachSummary.total,
                  outreachSummary: study.outreachSummary,
                  responseSummary: study.responseSummary,
                  subjectSummary: study.subjectSummary,
                }}
                refresh={refresh}
                setRefresh={setRefresh}
                totalInfo={true}
              />
            )}
          </div>
        );
      }
    },
    minWidth: 110,
    flex: 1,
  },
  {
    field: "responded",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnResponded" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.responseSummary.responded}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {study && study.responseSummary.responded}
          </MuiTypography>
        );
      }
    },
    minWidth: 110,
    flex: 1,
  },
  {
    field: "interested",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnInterested" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.responseSummary.interested}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {study && study.responseSummary.interested}
          </MuiTypography>
        );
      }
    },
    minWidth: 110,
    flex: 1,
    cellClassName: "interested-column",
  },
  {
    field: "notInterested",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnNotInterested" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.responseSummary.notInterested}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {study && study.responseSummary.notInterested}
          </MuiTypography>
        );
      }
    },
    minWidth: 100,
    flex: 1,
  },
  {
    field: "unsubscribed",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnUnsubscribed" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.responseSummary.unsubscribed}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {study && study.responseSummary.unsubscribed}
          </MuiTypography>
        );
      }
    },
    minWidth: 130,
    flex: 1,
    cellClassName: "unsubscribed-column",
  },
  {
    field: "pepOnStudy",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnPepOnStudy" />
      </MuiTypography>
    ),
    align: "center",
    renderCell: (params) => {
      if (!params.row.isTotal) {
        const { row } = params;
        return (
          <MuiTypography sx={multiLineCell}>
            {row.subjectSummary.pepEnrolled}
          </MuiTypography>
        );
      } else {
        return (
          <MuiTypography sx={multiLineCell}>
            {study && study.subjectSummary.pepEnrolled}
          </MuiTypography>
        );
      }
    },
    minWidth: 90,
    flex: 1,
  },
  {
    field: "note",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnNote" />
      </MuiTypography>
    ),
    renderCell: (params) => {
      if (!params.row.isTotal) {
        return <MuiTypography sx={multiLineCell}>{params.value}</MuiTypography>;
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
    minWidth: 200,
    flex: 1,
  },
  {
    field: "actions",
    headerAlign: "left",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnActions" />
      </MuiTypography>
    ),
    type: "actions",
    align: "center",
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      if (
        !params.row.isTotal &&
        params.row.generationType === "HONEST_BROKER_GENERATED"
      ) {
        const { row } = params;
        return (
          <>
            {row.status === OutreachStatuses.SCHEDULED ? (
              <MuiTooltip
                title={t("ConductOutreachPage.rowTooltipCancelIcon")}
                arrow
              >
                <MuiIconButton
                  size="small"
                  onClick={() => {
                    handleClickCancel();
                    setCancelOutreachRow(row);
                  }}
                >
                  <MuiCancelIcon fontSize="small" />
                </MuiIconButton>
              </MuiTooltip>
            ) : (
              <MuiIconButton size="small">
                <MuiCancelIcon
                  sx={conductOutreachPageContentStyles.disabledIconState}
                  fontSize="small"
                />
              </MuiIconButton>
            )}
          </>
        );
      } else {
        return <MuiTypography sx={multiLineCell}>{""}</MuiTypography>;
      }
    },
  },
];
